import api from '../api';
import type { GetMembershipsRequest, MembershipOutput } from '@ilteducation/customer-mgmt-types';
import { ref } from 'vue';
const defaultOptions: GetMembershipsRequest = {
  pageSize: 100,
  pageNumber: 1,
};

// Userville can't order memberships by user name, and we fetch every page anyway.
const byUserName = (a: MembershipOutput, b: MembershipOutput) => a.user.name.localeCompare(b.user.name);

export const useGroupMembers = () => {
  const members = ref<MembershipOutput[] | null>(null);
  const error = ref<null | unknown>(null);
  const isLoading = ref<boolean>(false);

  const loadAllMembers = async (groupId: string) => {
    isLoading.value = true;
    members.value = null;
    error.value = null;

    try {
      const membersResponse = await fetchAllMemebers(groupId, defaultOptions);
      members.value = membersResponse;
    } catch (err: unknown) {
      error.value = err;
    } finally {
      isLoading.value = false;
    }

    return members.value;
  };

  const fetchAllMemebers = async (
    groupUid: string,
    options: GetMembershipsRequest = defaultOptions,
  ): Promise<MembershipOutput[]> => {
    const request = (pageNumber: number) =>
      api.memberships.get({
        ...options,
        pageNumber,
        filters: { ...(options.filters ?? {}), groups: [groupUid] },
      });

    const response = await request(options.pageNumber ?? 1);

    const { data } = response.data;
    const total = response.data.meta.totalItems ?? 0;
    const diff = total - data.length;
    if (diff > 0) {
      const chunkCount = Math.ceil(diff / 100);
      const results = await Promise.all(Array.from({ length: chunkCount }, (_, index) => request(index + 2)));
      data.push(...results.map((r) => r.data.data).flat());
    }

    return data.sort(byUserName);
  };

  return {
    members,
    loadAllMembers,
    fetchAllMemebers,
    isLoading,
    error,
  };
};
